<template>
  <div class="dashboard-container">
    <!-- 欢迎区域 -->
    <div class="welcome-section">
      <div class="welcome-content">
        <div class="welcome-icon">🦐</div>
        <div class="welcome-text">
          <h1>欢迎回来，管理员</h1>
          <p class="welcome-subtitle">虾塘养殖管理系统 · 让养殖更智能</p>
          <div class="system-status">
            <span class="status-item">
              <span class="status-dot online"></span>
              系统运行正常
            </span>
          </div>
        </div>
      </div>
      <div class="welcome-time">
        <div class="time-display">{{ currentTime }}</div>
        <div class="date-display">{{ currentDate }}</div>
      </div>
    </div>

    <!-- 快捷功能区域 -->
    <div class="section-title">
      <span class="section-icon">⚡</span>
      快捷功能
    </div>
    <div class="quick-actions-grid">
      <el-card class="quick-card map-card" @click="navigateTo('pond-map')">
        <div class="card-bg-icon">🗺️</div>
        <div class="quick-card-content">
          <div class="quick-icon">🗺️</div>
          <div class="quick-title">地图视图</div>
          <div class="quick-desc">查看虾塘地图分布</div>
        </div>
      </el-card>

      <el-card
        class="quick-card result-card"
        @click="navigateTo('device-results')"
      >
        <div class="card-bg-icon">📋</div>
        <div class="quick-card-content">
          <div class="quick-icon">📋</div>
          <div class="quick-title">检测结果</div>
          <div class="quick-desc">查看设备检测数据</div>
        </div>
      </el-card>
    </div>

    <!-- 功能模块区域 -->
    <div class="section-title">
      <span class="section-icon">🛠️</span>
      功能模块
    </div>
    <div class="modules-grid">
      <el-card class="module-card" @click="navigateTo('users')">
        <div class="module-icon-wrapper user-bg">
          <div class="module-icon">👤</div>
        </div>
        <div class="module-info">
          <div class="module-title">用户管理</div>
          <div class="module-desc">管理系统用户信息</div>
        </div>
        <div class="module-arrow">→</div>
      </el-card>

      <el-card class="module-card" @click="navigateTo('device-videos')">
        <div class="module-icon-wrapper video-bg">
          <div class="module-icon">📹</div>
        </div>
        <div class="module-info">
          <div class="module-title">设备视频</div>
          <div class="module-desc">查看设备监控视频</div>
        </div>
        <div class="module-arrow">→</div>
      </el-card>

      <el-card class="module-card" @click="navigateTo('devices')">
        <div class="module-icon-wrapper device-bg">
          <div class="module-icon">📱</div>
        </div>
        <div class="module-info">
          <div class="module-title">装置管理</div>
          <div class="module-desc">管理养殖设备</div>
        </div>
        <div class="module-arrow">→</div>
      </el-card>

      <el-card class="module-card" @click="navigateTo('ponds')">
        <div class="module-icon-wrapper pond-bg">
          <div class="module-icon">🦐</div>
        </div>
        <div class="module-info">
          <div class="module-title">虾塘装置管理</div>
          <div class="module-desc">管理虾塘和养殖设备</div>
        </div>
        <div class="module-arrow">→</div>
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { ref, onMounted, onUnmounted } from "vue";
import { listDevicesByUserId } from "@/api/device";

const router = useRouter();

const currentTime = ref("");
const currentDate = ref("");
const onlineDeviceCount = ref(0);
let timer = null;
let deviceTimer = null;

const updateTime = () => {
  const now = new Date();
  currentTime.value = now.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  currentDate.value = now.toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  });
};

// 获取设备数量
const fetchDeviceCount = async () => {
  try {
    const response = await listDevicesByUserId();
    if (response.code === 0 && response.data) {
      onlineDeviceCount.value = response.data.length || 0;
    }
  } catch (error) {
    console.error("获取设备数量失败:", error);
  }
};

onMounted(() => {
  updateTime();
  timer = setInterval(updateTime, 1000);

  // 获取设备数量并定时刷新
  fetchDeviceCount();
  deviceTimer = setInterval(fetchDeviceCount, 30000); // 每30秒刷新一次
});

onUnmounted(() => {
  if (timer) {
    clearInterval(timer);
  }
  if (deviceTimer) {
    clearInterval(deviceTimer);
  }
});

const navigateTo = (page) => {
  ElMessage.info(`跳转到${page}页面`);
  router.push(`/${page}`);
};
</script>

<style scoped>
.dashboard-container {
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  min-height: 100vh;
}

/* 欢迎区域样式 */
.welcome-section {
  background: linear-gradient(135deg, #1b9a7c 0%, #2db38f 100%);
  border-radius: 16px;
  padding: 32px 40px;
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 8px 32px rgba(27, 154, 124, 0.3);
  position: relative;
  overflow: hidden;
}

.welcome-section::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -10%;
  width: 400px;
  height: 400px;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 70%
  );
  border-radius: 50%;
}

.welcome-content {
  display: flex;
  align-items: center;
  gap: 24px;
  z-index: 1;
}

.welcome-icon {
  font-size: 64px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.welcome-text h1 {
  margin: 0 0 8px 0;
  color: white;
  font-size: 28px;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.welcome-subtitle {
  margin: 0 0 16px 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
}

.system-status {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 14px;
  background: rgba(255, 255, 255, 0.15);
  padding: 6px 12px;
  border-radius: 20px;
  backdrop-filter: blur(10px);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-dot.online {
  background: #67c23a;
  box-shadow: 0 0 8px #67c23a;
}

.status-dot.warning {
  background: #e6a23c;
  box-shadow: 0 0 8px #e6a23c;
}

.welcome-time {
  text-align: right;
  color: white;
  z-index: 1;
}

.time-display {
  font-size: 36px;
  font-weight: 300;
  letter-spacing: 2px;
  margin-bottom: 4px;
  font-family: "Courier New", monospace;
}

.date-display {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
}

/* 分区标题样式 */
.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-icon {
  font-size: 20px;
}

/* 快捷功能区域样式 */
.quick-actions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}

.quick-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  border-radius: 12px;
}

.quick-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.map-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.result-card {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.card-bg-icon {
  position: absolute;
  right: -20px;
  bottom: -20px;
  font-size: 120px;
  opacity: 0.15;
  transform: rotate(-15deg);
}

.quick-card-content {
  position: relative;
  z-index: 1;
  padding: 24px;
  color: white;
  text-align: center;
}

.quick-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.quick-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
}

.quick-desc {
  font-size: 14px;
  opacity: 0.9;
}

/* 功能模块区域样式 */
.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.module-card {
  display: flex;
  align-items: center;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px;
  border: 1px solid #ebeef5;
}

.module-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: #1b9a7c;
}

.module-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.user-bg {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.video-bg {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.device-bg {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.pond-bg {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.module-icon {
  font-size: 28px;
}

.module-info {
  flex: 1;
}

.module-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.module-desc {
  font-size: 13px;
  color: #909399;
}

.module-arrow {
  font-size: 20px;
  color: #c0c4cc;
  transition: all 0.3s;
}

.module-card:hover .module-arrow {
  color: #1b9a7c;
  transform: translateX(4px);
}

/* 响应式适配 */
@media (max-width: 768px) {
  .dashboard-container {
    padding: 16px;
  }

  .welcome-section {
    flex-direction: column;
    text-align: center;
    padding: 24px;
  }

  .welcome-content {
    flex-direction: column;
    margin-bottom: 20px;
  }

  .welcome-time {
    text-align: center;
  }

  .system-status {
    justify-content: center;
  }

  .quick-actions-grid {
    grid-template-columns: 1fr;
  }

  .modules-grid {
    grid-template-columns: 1fr;
  }
}
</style>
